import mixpanel, { Dict, Mixpanel } from "mixpanel-browser";

import AnalyticsLogger from "./AnalyticsLogger";
import { BaseAnalyticsClient } from "./BaseAnalyticsClient";
import { AnalyticsAutoProps, AnalyticsEventKey, AnalyticsEvents, AnalyticsEventsSchema } from "./types";

export class MixpanelClient extends BaseAnalyticsClient {
  constructor(schema: AnalyticsEventsSchema, logger: AnalyticsLogger, apiToken: string = "no_mixpanel_token") {
    super(schema, logger);
    this.initializeMixpanel(apiToken);
  }

  private initializeMixpanel(apiToken: string): void {
    const isMissingApiToken = apiToken === "no_mixpanel_token" || !apiToken;
    const isLocalEnv = process.env.REACT_APP_STAGE === "local" || window.location.hostname === "localhost";
    const isLocalDevTesting = process.env.REACT_APP_MIXPANEL_DEVELOPER_TESTING_ENABLED === "true";

    if (isLocalDevTesting && !isMissingApiToken) {
      this.logger.warn("You are sending events to mixpanel from your local environment!".toUpperCase());
    }

    mixpanel.init(apiToken, {
      loaded: (mp: Mixpanel) => {
        // avoid enabling mixpanel in local environments unless explicitly testing
        if (isMissingApiToken || (isLocalEnv && !isLocalDevTesting)) {
          mp.disable();
        }
      },
    });
  }

  protected sendEvent<T extends AnalyticsEventKey>(event: T, properties?: AnalyticsEvents[T], autoProps?: AnalyticsAutoProps): void {
    const props = { ...autoProps, ...properties };
    mixpanel.track(event, props);
    this.setProfile({ $last_seen: new Date().toISOString() }); // make sure the 'Last Seen' property always reflects the latest event
  }

  /**
   * Identifies the current user to mixpanel. All subsequent events will be attributed to this user.
   * @param {string} identifier - a unique id for a user
   */
  public identify(identifier: string): void {
    mixpanel.identify(identifier);
  }

  /**
   * Set or update properties on a user record, i.e. {'gender': 'm'}.
   * @param {Object} profile - object containing profile properties
   */
  public setProfile(profile: Dict): void {
    mixpanel.people.set(profile);
  }

  /**
   * Reassigns an unidentified user to a new id.
   * @param {string} alias - A unique identifier that you want to use for this going forward.
   */
  public alias(newId: string): void {
    mixpanel.alias(newId);
  }

  /**
   * Used on sign out. Subsequent events will be anonymous until identify is called again.
   */
  public reset(): void {
    super.reset();
    mixpanel.reset();
  }
}
